import {
    NAV_ITEM_TYPE_ITEM,
    NAV_ITEM_TYPE_COLLAPSE,
    NAV_ITEM_TYPE_TITLE,
} from 'constants/navigation.constant'

const isLeaf = (node) => node?.type === NAV_ITEM_TYPE_ITEM
const isContainer = (node) =>
    node?.type === NAV_ITEM_TYPE_TITLE || node?.type === NAV_ITEM_TYPE_COLLAPSE

/**
 * Recursively prune the navigation tree given a `{ key: boolean }` visibility map.
 *
 * - Only NAV_ITEM_TYPE_ITEM entries are toggleable. A leaf is dropped iff
 *   `visibilityMap[key] === false` — anything else (true, undefined, null) keeps
 *   it visible. This is the "default visible" contract: new menu items added by
 *   developers are shown until explicitly hidden.
 * - NAV_ITEM_TYPE_TITLE / NAV_ITEM_TYPE_COLLAPSE entries are auto-dropped when
 *   their subMenu becomes empty after pruning.
 */
export const filterNavigationByVisibility = (navigationConfig, visibilityMap) => {
    const map = visibilityMap || {}
    const isHidden = (key) => map[key] === false

    const walk = (nodes) =>
        (nodes || []).flatMap((node) => {
            if (isLeaf(node)) {
                return isHidden(node.key) ? [] : [node]
            }
            if (isContainer(node)) {
                const subMenu = walk(node.subMenu)
                if (!subMenu.length) return []
                return [{ ...node, subMenu }]
            }
            return [node]
        })

    return walk(navigationConfig)
}

/**
 * Walk the navigation tree and produce a flat, grouped structure suitable for
 * rendering toggle UI. Output:
 *
 *   [{
 *     section: { key, title, translateKey },
 *     groups: [
 *       { collapse: null,    items: [<leaf>, ...] },
 *       { collapse: <node>,  items: [<leaf>, ...] },  // for NAV_ITEM_TYPE_COLLAPSE
 *     ]
 *   }, ...]
 *
 * Top-level NAV_ITEM_TYPE_ITEM nodes are placed under a synthetic section so
 * the UI doesn't need a special case.
 */
export const collectToggleableItems = (navigationConfig) => {
    const sections = []

    const collectLeavesUnderCollapse = (collapseNode) => {
        const out = []
        const walk = (nodes) => {
            (nodes || []).forEach((n) => {
                if (isLeaf(n)) out.push(n)
                else if (isContainer(n)) walk(n.subMenu)
            })
        }
        walk(collapseNode.subMenu)
        return out
    }

    ;(navigationConfig || []).forEach((top) => {
        if (top?.type === NAV_ITEM_TYPE_TITLE) {
            const groups = []
            let directItems = []

            ;(top.subMenu || []).forEach((child) => {
                if (isLeaf(child)) {
                    directItems.push(child)
                } else if (child?.type === NAV_ITEM_TYPE_COLLAPSE) {
                    if (directItems.length) {
                        groups.push({ collapse: null, items: directItems })
                        directItems = []
                    }
                    const items = collectLeavesUnderCollapse(child)
                    if (items.length) {
                        groups.push({ collapse: child, items })
                    }
                }
            })

            if (directItems.length) {
                groups.push({ collapse: null, items: directItems })
            }

            if (groups.length) {
                sections.push({
                    section: {
                        key: top.key,
                        title: top.title,
                        translateKey: top.translateKey,
                    },
                    groups,
                })
            }
        } else if (isLeaf(top)) {
            sections.push({
                section: {
                    key: top.key,
                    title: top.title,
                    translateKey: top.translateKey,
                },
                groups: [{ collapse: null, items: [top] }],
            })
        }
    })

    return sections
}
